import {uuid, VeboxModels} from "../../../../../types/veboxModels";
import {JSONCensusEquipment} from "../models/JSONCensusEquipment";
import {RestDataLoader} from "../../../../../dashboard/widgets/unilever/RestDataLoader";
import {PageOf} from "../models/PageOf";
import {JSONCensus} from "../models/JSONCensus";
import {EquipmentFilterV7} from "../models/EquipmentFilters";
import {JSONList} from "../models/JSONList";
import {
    CensusEquipmentStatusDetailsEnum,
    CensusEquipmentStatusesEnum,
    CensusEquipmentStatusReasons,
} from "../models/CensusEnums";
import {JSONCensusEquipmentsActionsConfirmation} from "../models/JSONCensusEquipmentsActionsConfirmation";
import {JSONCensusCSVImportResult} from "../models/JSONCensusCSVImportResult";
import {VeBoxEquipmentFilters} from "../models/VeBoxEquipmentFilters";

export const BASE_URL = "census/";
export const getBaseUrl = (uuid?: uuid) =>
    `${BASE_URL}${uuid ? `${uuid}/` : ""}`;

export interface PageOfCensusEquipmentParam {
    guid: uuid;
    page: number;
    size: number;
    status?: CensusEquipmentStatusesEnum;
    statusDetail?: CensusEquipmentStatusDetailsEnum;
    statusReason?: CensusEquipmentStatusReasons;
    shouldFetch?: boolean;
}

export interface PageOfEquipmentWithFilterParam {
    limit?: number;
    offset?: number;
    filterGuid?: uuid;
    shouldFetch?: boolean;
}

export const fetchCensusEntries = async (
    params: PageOfCensusEquipmentParam,
): Promise<PageOf<JSONCensusEquipment>> => {
    return RestDataLoader.fetchData({
        url: `${getBaseUrl(params.guid)}entries`,
        params: {page: params.page, size: params.size},
    });
};

export const fetchCensusEntriesByInventoryStatus = async (
    params: PageOfCensusEquipmentParam,
): Promise<PageOf<JSONCensusEquipment>> => {
    return RestDataLoader.fetchData({
        url: `${getBaseUrl(params.guid)}entriesByInventoryStatus`,
        params: {
            page: params.page,
            size: params.size,
            status: params?.status,
            statusDetail: params?.statusDetail,
            statusReason: params?.statusReason,
        },
    });
};

export const fetchEquipmentList = async (params: {
    limit: number;
    offset: number;
    filterGuid: uuid;
}): Promise<JSONList<VeboxModels.JSONEquipment>> => {
    return RestDataLoader.fetchData({
        url: `equipment/`,
        params: {
            limit: params?.limit,
            offset: params?.offset,
            filterGuid: params?.filterGuid,
        },
    });
};

export function createNewCensus(): Promise<JSONCensus> {
    return new Promise<JSONCensus>((res, rej) => {
        RestDataLoader.postJSON(`${getBaseUrl()}`, {}, res, rej);
    });
}

export function createEquipmentFilter(
    filter: VeBoxEquipmentFilters,
): Promise<EquipmentFilterV7> {
    return new Promise<EquipmentFilterV7>((res, rej) => {
        RestDataLoader.postJSON(
            `filters/equipment`,
            {fullFilter: JSON.stringify(filter)},
            res,
            rej,
        );
    });
}

export function planAnalysisForSuggestedActions(
    censusGuid: uuid,
    periodStart: string,
    periodEnd: string,
): Promise<void> {
    return new Promise<void>((res, rej) => {
        RestDataLoader.postJSON(
            `${getBaseUrl(
                censusGuid,
            )}planAnalysisForSuggestedActions?periodStart=${periodStart}&periodEnd=${periodEnd}`,
            {},
            res,
            rej,
        );
    });
}

export function addEntriesToCensus(
    censusGuid: uuid,
    componentGuids: uuid[],
): Promise<JSONCensus> {
    return new Promise<JSONCensus>((res, rej) => {
        RestDataLoader.postJSON(
            `${getBaseUrl(censusGuid)}entries`,
            componentGuids,
            res,
            rej,
        );
    });
}

export function addEntriesToCensusByFile(
    file: File,
): Promise<JSONCensusCSVImportResult> {
    return new Promise<JSONCensusCSVImportResult>((res, rej) => {
        const data = new FormData();
        data.append("file", file);
        RestDataLoader.fetchWithAuthentication(
            `${RestDataLoader.getBaseUrl()}v7/${getBaseUrl()}entries/import`,
            RestDataLoader.getCurrentToken(),
            {
                method: "POST",
                body: data,
                // @ts-ignore
                encoding: "utf-8",
            },
        )
            .then((response) => {
                if (response.status === 200) {
                    response.json().then(res);
                } else {
                    rej(response);
                }
            })
            .catch(rej);
    });
}

export function removeEntriesToCensus(
    censusGuid: uuid,
    componentGuids?: uuid[],
    deleteAll: boolean = false,
): Promise<JSONCensus> {
    return new Promise<JSONCensus>((res, rej) => {
        RestDataLoader.deleteJSON(
            `${getBaseUrl(censusGuid)}entries?deleteAll=${deleteAll}`,
            componentGuids ? {entryGuids: componentGuids} : null,
            res,
            rej,
        );
    });
}

export function actionsConfirmations(
    censusGuid: uuid,
    actionsConfirmations: JSONCensusEquipmentsActionsConfirmation[],
): Promise<void> {
    return new Promise<void>((res, rej) => {
        RestDataLoader.postJSON(
            `${getBaseUrl(censusGuid)}planActionsExecution`,
            actionsConfirmations,
            res,
            rej,
        );
    });
}


export function deleteCensus(
    censusGuid: uuid,
): Promise<JSONCensus> {
    return new Promise<JSONCensus>((res, rej) => {
        RestDataLoader.deleteJSON(
            `${getBaseUrl(censusGuid)}`,
            {},
            res,
            rej,
        );
    });
}